import { Alert, Link, Stack, Typography } from '@mui/material';
import {
  FTextInput,
  LoadingPromiseButtonAdvanced,
} from '@pflegenavi/web-components';
import { Formik, useFormikContext } from 'formik';
import type { FC } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthentication as useWrappedAuthentication } from '@pflegenavi/frontend/authentication';
import {
  RequiredActions,
  shouldUseNewLogin,
  useAuthentication,
} from '@pflegenavi/shared-frontend/authentication';
import { getPhoenixApiBaseUrl } from '@pflegenavi/shared-frontend/platform';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import PflegenaviLogo from './assets/pflegenavi_logo.svg';
import { PageContainer } from './components/PageContainer';
import { RequiredResetPassword } from './RequiredResetPassword';
import { ResetPassword } from './ResetPassword';
import { ResetPasswordConfirm } from './ResetPasswordConfirm';
import { TermsAndConditions } from './TermsAndConditions';
import { Welcome } from './Welcome';

import { has as objectHasKey } from 'lodash';
import { PasswordField } from './components/PasswordField';
import { VerifyEmail } from './VerifyEmail';

interface IFormValues {
  email: string;
  password: string;
}

interface LoginProps {
  /**
   * When true, the login process will first check the login method after email entry
   * using a two-step process
   */
  useEmailFirstLogin?: boolean;
}

export const Login: FC<LoginProps> = ({ useEmailFirstLogin = false }) => {
  const navigate = useNavigate();
  const auth = useAuthentication(shouldUseNewLogin());
  const wrappedAuth = useWrappedAuthentication();

  const comingFromLogin = useRef(false);

  useEffect(() => {
    if (auth?.redirectUri && comingFromLogin.current) {
      const url = new URL(auth.redirectUri);
      // TODO: We might want to compare hostname
      navigate(url.pathname);
    } else if (
      auth?.requiredActions.includes(RequiredActions.UPDATE_PASSWORD)
    ) {
      navigate('/required/reset-password?pn-login');
    } else if (
      auth?.requiredActions.includes(RequiredActions.TERMS_AND_CONDITIONS)
    ) {
      navigate('/required/terms-and-conditions?pn-login');
    } else if (auth?.requiredActions.includes(RequiredActions.VERIFY_EMAIL)) {
      navigate('/required/verify-email?pn-login');
    }
  }, [auth?.requiredActions, navigate, auth?.redirectUri]);

  useEffect(() => {
    // If the token includes actions, redirect the user based on those actions
    // This might be a redirect URI instead? OR we forward to the parent based on names
  }, []);

  // Due to re-renderings, we might still be in Login but actually already have an user.
  // In this case we need to render null, as rendering the Routes will cause a redirect to the dashboard when
  // refreshing the page
  const userLoggedIn = auth?.token || wrappedAuth.user;
  if (auth?.initializing || userLoggedIn) {
    return null;
  }

  // The user saw a login screen or similar
  comingFromLogin.current = true;

  return (
    <Routes>
      <Route path={'/'}>
        <Route
          index
          element={<LoginScreen useEmailFirstLogin={useEmailFirstLogin} />}
        />
        <Route path={'/welcome'} element={<Welcome />} />
        <Route
          path={'/reset-password/:token'}
          element={<ResetPasswordConfirm />}
        />
        <Route path={'/reset-password'} element={<ResetPassword />} />
        <Route
          path={'/required/reset-password'}
          element={<RequiredResetPassword />}
        />
        <Route
          path={'/required/terms-and-conditions'}
          element={<TermsAndConditions />}
        />
        <Route path={'/required/verify-email'} element={<VerifyEmail />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

interface LoginScreenProps {
  useEmailFirstLogin?: boolean;
}

export const LoginScreen: FC<LoginScreenProps> = ({
  useEmailFirstLogin = false,
}) => {
  const { t } = useTranslation();
  const auth = useAuthentication(shouldUseNewLogin());

  const { state, search } = useLocation();
  const success = objectHasKey(state, 'success')
    ? (
        state as {
          success: boolean;
        }
      ).success
    : false;

  const [error, setError] = useState<string | undefined>(undefined);
  const [showPassword, setShowPassword] = useState<boolean>(
    !useEmailFirstLogin
  );
  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const initialValues = useMemo<IFormValues>(() => {
    return {
      email: '',
      password: '',
    };
  }, []);

  // Handler for email first flow
  const handleEmailSubmit = useCallback(
    async (values: IFormValues) => {
      if (!useEmailFirstLogin || !auth) {
        return;
      }

      const enableSAML =
        new URLSearchParams(search).get('enableSAML') === 'true';

      setIsCheckingEmail(true);
      try {
        if (enableSAML) {
          const result = await auth.samlLookup(values.email);
          if (result.loginType === 'REDIRECT' && result.idpId) {
            const returnTo = window.location.pathname + window.location.search;
            const apiUrl = getPhoenixApiBaseUrl(undefined);
            const encodedIdpId = encodeURIComponent(result.idpId);
            window.location.href = `${apiUrl}/sso/web/auth/${encodedIdpId}?email=${encodeURIComponent(
              values.email
            )}&return_to=${encodeURIComponent(returnTo)}`;
            return;
          }
        } else {
          const result = await auth.loginMethod(values.email);
          if (result.loginType === 'REDIRECT' && result.redirectUrl) {
            window.location.href = result.redirectUrl;
            return;
          }
        }

        // Show password field for PASSWORD login type
        setShowPassword(true);
        // Focus the password field after it appears
        setTimeout(() => {
          passwordRef.current?.focus();
        }, 100);
      } catch {
        setError(t('authentication.errors.something-went-wrong'));
      } finally {
        setIsCheckingEmail(false);
      }
    },
    [auth, t, useEmailFirstLogin, search]
  );

  const onSubmit = useCallback(
    async (values: IFormValues) => {
      // In email first flow, first check login method if password is not visible yet
      if (useEmailFirstLogin && !showPassword) {
        await handleEmailSubmit(values);
        return;
      }

      // Standard login with password
      const result = await auth?.login(values.email, values.password);
      if (result && 'status' in result && result.status === 'error') {
        if (result.errorMessage === 'User is disabled') {
          setError(t('authentication.errors.account-disabled'));
        } else {
          setError(t('authentication.errors.username-or-password-incorrect'));
        }
      }
    },
    [auth, t, useEmailFirstLogin, showPassword, handleEmailSubmit]
  );

  return (
    <PageContainer>
      <Stack gap={3} width={550} alignItems="center">
        <img src={PflegenaviLogo} width={360} />
        <Stack gap={1} width="100%">
          {error && (
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          )}
          {success && (
            <Alert severity="success">
              {' '}
              {t('authentication.reset-password-success')}
            </Alert>
          )}
          <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => {
              return (
                <form
                  onSubmit={handleSubmit}
                  style={{
                    maxWidth: 560,
                    width: '100%',
                  }}
                >
                  <LoginForm
                    showPassword={showPassword}
                    isCheckingEmail={isCheckingEmail}
                    passwordRef={passwordRef}
                    useEmailFirstLogin={useEmailFirstLogin}
                    setShowPassword={setShowPassword}
                  />
                </form>
              );
            }}
          </Formik>
        </Stack>
      </Stack>
    </PageContainer>
  );
};

interface LoginFormProps {
  showPassword: boolean;
  isCheckingEmail: boolean;
  passwordRef: React.RefObject<HTMLInputElement | null>;
  useEmailFirstLogin?: boolean;
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoginForm: FC<LoginFormProps> = ({
  showPassword,
  isCheckingEmail,
  passwordRef,
  useEmailFirstLogin = false,
  setShowPassword,
}) => {
  const { t } = useTranslation();
  const formik = useFormikContext<IFormValues>();
  const { values, setFieldValue } = formik;

  const navigate = useNavigate();

  const onResetPassword = useCallback(() => {
    navigate('/reset-password?pn-login');
  }, [navigate]);

  // Create a wrapped submit function that returns a promise
  // and handles the loading state internally
  const handleSubmit = useCallback(async () => {
    if (isCheckingEmail) {
      // Skip if already checking email
      return;
    }
    await formik.submitForm();
  }, [formik, isCheckingEmail]);

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <FTextInput
          name="email"
          placeholder={t('common.email-address')}
          formik={formik}
          sx={{
            outlineColor: 'red',
          }}
          disabled={isCheckingEmail || (useEmailFirstLogin && showPassword)}
        />

        {/* Password field with animation */}
        <div
          style={{
            maxHeight: showPassword ? '100px' : '0',
            overflow: 'hidden',
            transition: 'max-height 0.3s ease-in-out',
          }}
        >
          <PasswordField
            name="password"
            placeholder={t('common.password')}
            value={values.password}
            onChange={(e) => setFieldValue('password', e.target.value)}
            inputRef={passwordRef}
            fullWidth={true}
          />
        </div>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {useEmailFirstLogin && showPassword ? (
            <Link
              onClick={() => {
                setShowPassword(false);
                setFieldValue('password', '');
              }}
              sx={{
                cursor: 'pointer',
              }}
            >
              {t('common.change-email')}
            </Link>
          ) : (
            <div />
          )}
          <Link
            onClick={onResetPassword}
            sx={{
              cursor: 'pointer',
            }}
          >
            {t('authentication.forgot-password')}
          </Link>
        </Stack>
      </Stack>
      <LoadingPromiseButtonAdvanced
        variant="contained"
        type="submit"
        onClick={handleSubmit}
        sx={{
          py: 1.8,
          fontSize: 16,
        }}
      >
        {useEmailFirstLogin && !showPassword
          ? t('actions.continue')
          : t('authentication.login')}
      </LoadingPromiseButtonAdvanced>
    </Stack>
  );
};
